/**
 * The reference listing's "Description" cell: where a ref's description came
 * from, as a dot + label in the same shape as the Module-link cell beside it.
 *
 *   AI      emerald  rewritten by an AI pass
 *   Manual  gray     hand-edited; AI passes leave it alone
 *   –       dash     scraped or submitted at import, not yet rewritten
 *   Legacy  amber    no provenance recorded; treated as machine-made
 *   ?       dash     the listing's source does not say (see `absentMeans`)
 */
import type { Timestamp } from 'firebase/firestore';

export type DescriptionProvenance = 'ai' | 'manual' | 'imported' | 'legacy' | 'unknown';

/**
 * Classify a stored `descriptionSource`.
 *
 * A Firestore document that lacks the field is a legacy ref. A search hit that
 * lacks it is NOT known to be one: the index only carries what the indexer
 * projects, and an attribute it does not project is absent from every hit. So
 * the caller has to say what absence means for its source. `'legacy'` is also
 * accepted as a stored value, so an indexer can state it outright for the refs
 * that have no provenance rather than leave the attribute off.
 */
export function describeDescriptionProvenance(
  source: string | null | undefined,
  absentMeans: 'legacy' | 'unknown',
): DescriptionProvenance {
  if (source === 'ai' || source === 'manual' || source === 'imported' || source === 'legacy') {
    return source;
  }
  return absentMeans;
}

interface DescriptionSourceBadgeProps {
  source: string | null | undefined;
  /** What a missing `source` means for the row's data source. */
  absentMeans: 'legacy' | 'unknown';
  /** When the description was last AI-rewritten, for the AI badge's tooltip. */
  rewrittenAt?: Timestamp;
}

function formatRewrittenAt(rewrittenAt: Timestamp | undefined): string {
  // Guarded: a value that has been through a cache or a spread may no longer be
  // a real Timestamp, and a tooltip is not worth a crashed row.
  if (!rewrittenAt || typeof rewrittenAt.toDate !== 'function') return '';
  return rewrittenAt.toDate().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function DescriptionSourceBadge({ source, absentMeans, rewrittenAt }: DescriptionSourceBadgeProps) {
  const provenance = describeDescriptionProvenance(source, absentMeans);
  switch (provenance) {
    case 'ai': {
      const when = formatRewrittenAt(rewrittenAt);
      return (
        <span
          className="inline-flex items-center gap-1 text-emerald-700"
          title={when ? `AI-enriched description, last rewritten ${when}` : 'AI-enriched description'}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          AI
        </span>
      );
    }
    case 'manual':
      return (
        <span
          className="inline-flex items-center gap-1 text-gray-600"
          title="Hand-edited description — AI rewrites leave it alone"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
          Manual
        </span>
      );
    case 'legacy':
      return (
        <span
          className="inline-flex items-center gap-1 text-amber-700"
          title="No provenance recorded — eligible for AI rewrite"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
          Legacy
        </span>
      );
    case 'imported':
      return (
        <span className="text-gray-300" title="Imported description — not yet AI-rewritten">
          -
        </span>
      );
    default:
      return (
        <span
          className="text-gray-300"
          title="The search index does not carry description provenance yet. Tick “Only refs needing AI enrichment” to read it from Firestore."
        >
          ?
        </span>
      );
  }
}
